import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  ConfirmationPopup,
  HiddenPasswordField,
  IconDelete,
  IconEdit,
  IconPassword,
  List,
  Notification,
} from 'shared/components';
import {
  displayValue,
  hasPermission,
  getCurrentUserPermissions,
} from 'shared/helpers';
import {
  getOrderManagers,
  removeOrderManager,
} from 'orders/OrderContainer/actions';
import { LicenseUserPasswordForm } from 'licenses/components';
import { BulkOrderManagersForm, OrderManagerForm } from './components';

const OrderManagers = ({ order, customerEmail: customerEmailProp }) => {
  const { t } = useTranslation();
  const permissionsList = useSelector((state) =>
    getCurrentUserPermissions(state),
  );
  const orderID = get(order, 'id');
  const customerEmail = customerEmailProp || get(order, 'customer.email');

  const [isLoading, setLoading] = useState(true);
  const [orderManagers, setOrderManagers] = useState([]);
  // add/edit manager form
  const [orderManagerToEdit, setOrderManagerToEdit] = useState(null);
  const [showOrderManagerForm, setOrderManagerFormDisplay] = useState(false);
  // remove manager
  const [orderManagerToRemove, setOrderManagerToRemove] = useState(null);
  const [showManagerRemovePopup, setManagerRemovePopupDisplay] = useState(null);
  const [isRemoveManagerLoading, setRemoveManagerLoading] = useState(false);
  // bulk add and remove
  const [showBulkAddForm, setBulkAddFormDisplay] = useState(false);
  const [selectedManagerIDs, setSelectedManagerIDs] = useState([]);
  const [showBulkRemovePopup, setBulkRemovePopupDisplay] = useState(false);
  const [isBulkRemoveLoading, setBulkRemoveLoading] = useState(false);
  // change manager pass
  const [showOrderManagerPasswordForm, setOrderManagerPasswordForm] =
    useState(false);
  const [orderManagerToPasswordChange, setOrderManagerToPasswordChange] =
    useState(null);

  const checkPermission = () => {
    const permission = hasPermission(permissionsList, 'manage_managers');
    if (!permission) {
      Notification(
        'error',
        t('Missing permission'),
        t('You do not have permission to perform this action.'),
      );
      return false;
    }
    return true;
  };

  const fetchManagers = useCallback(() => {
    getOrderManagers(orderID)
      .then((res) => {
        const data = get(res, 'data', []);
        setOrderManagers(data);
        setSelectedManagerIDs((selected) =>
          selected.filter((id) => data.some((om) => get(om, 'id') === id)),
        );
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [orderID]);

  useEffect(() => {
    fetchManagers();
  }, [fetchManagers]);

  const handleRemoveOrderManager = () => {
    const managerToRemoveID = get(orderManagerToRemove, 'id');
    setRemoveManagerLoading(true);

    removeOrderManager(orderID, managerToRemoveID)
      .then(() => {
        const newList = orderManagers.filter(
          (om) => get(om, 'id') !== managerToRemoveID,
        );
        setOrderManagers(newList);
        setSelectedManagerIDs((selected) =>
          selected.filter((id) => id !== managerToRemoveID),
        );
        setRemoveManagerLoading(false);
        setManagerRemovePopupDisplay(false);
        setOrderManagerToRemove(null);
        Notification('success', t('Changes saved successfully'));
      })
      .catch(() => {
        setRemoveManagerLoading(false);
        Notification(
          'error',
          t('Your changes were not saved'),
          t('There was an error while saving your changes'),
        );
      });
  };

  const isCustomer = (manager) => {
    const managerEmail = get(manager, 'original.true_email');
    return managerEmail === customerEmail;
  };

  const toggleManagerSelection = (managerID) => {
    setSelectedManagerIDs((selected) =>
      selected.includes(managerID)
        ? selected.filter((id) => id !== managerID)
        : selected.concat(managerID),
    );
  };

  const handleBulkAddClick = () => {
    if (!checkPermission()) {
      return false;
    }
    setBulkAddFormDisplay(true);
    return true;
  };

  const handleBulkRemoveClick = () => {
    if (!checkPermission()) {
      return false;
    }
    setBulkRemovePopupDisplay(true);
    return true;
  };

  const handleBulkRemove = async () => {
    setBulkRemoveLoading(true);
    const failed = [];

    for (const managerID of selectedManagerIDs) {
      try {
        await removeOrderManager(orderID, managerID);
      } catch (err) {
        failed.push(managerID);
      }
    }

    setBulkRemoveLoading(false);
    setBulkRemovePopupDisplay(false);
    setSelectedManagerIDs(failed);
    fetchManagers();

    if (failed.length) {
      const failedEmails = orderManagers
        .filter((om) => failed.includes(get(om, 'id')))
        .map((om) => get(om, 'true_email'))
        .join(', ');
      Notification(
        'error',
        t('Some license managers were not removed'),
        failedEmails,
      );
    } else {
      Notification(
        'success',
        t('Changes saved successfully'),
        t('License managers have been removed'),
      );
    }
  };

  const handleAddManager = () => {
    const isAllowed = checkPermission();
    if (!isAllowed) {
      return false;
    }
    setOrderManagerFormDisplay(true);
    return true;
  };

  const handlePasswordChangeClick = (rowData) => {
    const isAllowed = checkPermission();
    if (!isAllowed) {
      return false;
    }
    setOrderManagerToPasswordChange(rowData.original);
    setOrderManagerPasswordForm(true);
    return true;
  };

  const handleManagerEditClick = (rowData) => {
    const isAllowed = checkPermission();
    if (!isAllowed) {
      return false;
    }
    setOrderManagerToEdit(rowData.original);
    setOrderManagerFormDisplay(true);
    return true;
  };

  const handleManagerDeleteClick = (rowData) => {
    const isAllowed = checkPermission();
    if (!isAllowed) {
      return false;
    }
    setOrderManagerToRemove(rowData.original);
    setManagerRemovePopupDisplay(true);
    return true;
  };

  return (
    <div className="OrderManagers order-managers-tab">
      <div className="list-header">
        <div>
          <Button onClick={handleAddManager} theme="info" size="sm">
            {t('Add license manager')}
          </Button>
          <Button onClick={handleBulkAddClick} theme="default" size="sm">
            {t('Add managers in bulk')}
          </Button>
          <Button
            onClick={handleBulkRemoveClick}
            theme="default"
            size="sm"
            disabled={!selectedManagerIDs.length}
          >
            {t('Remove selected')}
          </Button>
        </div>
      </div>
      <List
        columns={[
          {
            id: 'select',
            className: 'select-container',
            Cell: (rowData) => (
              <button
                className="table-select"
                aria-label="Select license manager"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isCustomer(rowData)) {
                    toggleManagerSelection(rowData.original.id);
                  }
                }}
                type="button"
                disabled={isCustomer(rowData)}
              >
                <Checkbox
                  inputId={`${rowData.original.id}-select`}
                  checked={selectedManagerIDs.includes(rowData.original.id)}
                  disabled={isCustomer(rowData)}
                />
              </button>
            ),
            width: 60,
            sortable: false,
          },
          {
            accessor: 'true_email',
            Header: t('Email'),
            Cell: (cellData) => displayValue(cellData.value),
            minWidth: 200,
          },
          {
            accessor: 'first_name',
            Header: t('First Name'),
            Cell: (cellData) => displayValue(cellData.value),
          },
          {
            accessor: 'last_name',
            Header: t('Last Name'),
            Cell: (cellData) => displayValue(cellData.value),
          },
          {
            accessor: 'phone_number',
            Header: t('Phone number'),
            Cell: (cellData) => displayValue(cellData.value),
          },
          {
            accessor: 'is_initial_password',
            Header: t('Initial password'),
            className: 'text-center',
            headerClassName: 'text-center',
            Cell: (cellData) => {
              if (!cellData.value) {
                return t('N/A');
              }
              const initialPass = get(cellData, 'original.initial_password');
              return (
                <HiddenPasswordField value={initialPass} fallback={t('N/A')} />
              );
            },
          },
          {
            Header: t('Change password'),
            className: 'text-center',
            headerClassName: 'text-center',
            Cell: (rowData) => (
              <Button
                className="edit-button"
                onClick={() => handlePasswordChangeClick(rowData)}
                type="button"
              >
                <IconPassword height="24px" width="24px" viewBox="0 0 65 60" />
              </Button>
            ),
          },
          {
            Cell: (rowData) => (
              <Button
                className="edit-button"
                onClick={() => handleManagerEditClick(rowData)}
                type="button"
              >
                <IconEdit height="16px" width="16px" />
              </Button>
            ),
            maxWidth: 50,
          },
          {
            Cell: (rowData) => (
              <Button
                className="edit-button"
                onClick={() => handleManagerDeleteClick(rowData)}
                type="button"
                disabled={isCustomer(rowData)}
              >
                <IconDelete height="16px" width="16px" color="#ee5253" />
              </Button>
            ),
            maxWidth: 50,
          },
        ]}
        data={orderManagers}
        loading={isLoading}
        minRows={2}
        pageSize={20}
      />
      {showOrderManagerForm && (
        <OrderManagerForm
          orderID={orderID}
          closeCb={() => {
            setOrderManagerToEdit(null);
            setOrderManagerFormDisplay(false);
          }}
          confirmCb={() => {
            fetchManagers();
            setOrderManagerToEdit(null);
            setOrderManagerFormDisplay(false);
          }}
          orderManagers={orderManagers}
          manager={orderManagerToEdit}
        />
      )}
      {showBulkAddForm && (
        <BulkOrderManagersForm
          orderID={orderID}
          orderManagers={orderManagers}
          closeCb={() => setBulkAddFormDisplay(false)}
          confirmCb={fetchManagers}
        />
      )}
      {showBulkRemovePopup && (
        <ConfirmationPopup
          closeCb={() => setBulkRemovePopupDisplay(false)}
          confirmCb={handleBulkRemove}
          title={t(
            'Are you sure you want to remove the selected license managers?',
          )}
          confirmText={t('Remove')}
          theme="error"
          disabled={isBulkRemoveLoading}
        >
          {orderManagers
            .filter((om) => selectedManagerIDs.includes(get(om, 'id')))
            .map((om) => get(om, 'true_email'))
            .join(', ')}
        </ConfirmationPopup>
      )}
      {showOrderManagerPasswordForm && (
        <LicenseUserPasswordForm
          title={t('Change password')}
          closeCb={() => {
            setOrderManagerPasswordForm(false);
            setOrderManagerToPasswordChange(null);
          }}
          confirmCb={() => {
            fetchManagers();
            setOrderManagerPasswordForm(false);
            setOrderManagerToPasswordChange(null);
          }}
          licenseUser={orderManagerToPasswordChange}
        />
      )}
      {showManagerRemovePopup && (
        <ConfirmationPopup
          closeCb={() => {
            setOrderManagerToRemove(null);
            setManagerRemovePopupDisplay(false);
          }}
          confirmCb={handleRemoveOrderManager}
          title={t('Are you sure you want to remove this license manager?')}
          confirmText={t('Remove')}
          theme="error"
          disabled={isRemoveManagerLoading}
        >
          {get(orderManagerToRemove, 'true_email')}
        </ConfirmationPopup>
      )}
    </div>
  );
};

OrderManagers.propTypes = {
  order: PropTypes.object.isRequired,
  customerEmail: PropTypes.string,
};

OrderManagers.defaultProps = {
  customerEmail: null,
};

export default OrderManagers;
